// The frame every settings page uses (your settings at /settings, a server's at /s/:id/settings): a header with a
// way back, a list of sections on the left (a list you tap into on phones), and the chosen section on the right.
// Pages supply the sections; this only does layout and navigation, so all settings look and behave the same.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export interface SettingsSection {
  id: string;
  label: string;
  /** Small text under the label in the phone list (e.g. "Dark"). */
  hint?: string;
  /** Shown red, at the bottom of the list (e.g. "Leave server", "Delete account"). */
  danger?: boolean;
  /**
   * Sections are listed in runs by group: a separator comes before each new group, with its label when it has one
   * (Server settings: the settings, then "Stats"). Ungrouped sections are one group.
   */
  group?: string;
  /** The section's heading, when it should say more than its label in the list (e.g. "Stats" for Stats → Overview). */
  title?: string;
  groupLabel?: string;
  render: () => React.ReactNode;
}

export function SettingsShell({
  title,
  backTo,
  sections,
  initial,
}: {
  title: string;
  /** Where "back" / Esc goes when there's no history to return to. */
  backTo: string;
  sections: SettingsSection[];
  initial?: string;
}) {
  const navigate = useNavigate();
  const [active, setActive] = useState(initial ?? sections[0]?.id);
  /** Phones show either the list or one section; true once a section is opened. */
  const [open, setOpen] = useState(!!initial);
  const back = () => (window.history.length > 1 ? navigate(-1) : navigate(backTo));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      // Leave Esc to fields, menus and dialogs inside the page.
      const t = e.target as HTMLElement | null;
      if (t?.closest("input, textarea, select, [role=dialog], [role=menu], [role=listbox]")) return;
      back();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  const current = sections.find((s) => s.id === active) ?? sections[0];
  return (
    <div className={`settings${open ? " section-open" : ""}`}>
      <header className="settings-head">
        <button type="button" className="icon-btn head-btn" onClick={() => (open && window.matchMedia("(max-width: 760px)").matches ? setOpen(false) : back())} aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
        </button>
        <h1>{title}</h1>
        <span className="settings-esc desktop-only" aria-hidden="true">esc</span>
      </header>
      <div className="settings-body">
        <nav className="settings-nav" aria-label={`${title} sections`}>
          {groups(sections).map((g, i) => (
            <div key={g.id} className="settings-nav-group" role="group" aria-label={g.label}>
              {i > 0 && <div className="settings-nav-sep" role="separator" />}
              {g.label && <div className="settings-nav-label" aria-hidden="true">{g.label}</div>}
              <div className="settings-nav-list">
                {g.sections.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`settings-nav-item${s.id === current?.id ? " active" : ""}${s.danger ? " danger" : ""}`}
                    aria-current={s.id === current?.id ? "page" : undefined}
                    onClick={() => {
                      setActive(s.id);
                      setOpen(true);
                    }}
                  >
                    <span>{s.label}</span>
                    {s.hint && <span className="settings-hint">{s.hint}</span>}
                    <svg className="settings-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <main className="settings-main">
          {current && (
            <section aria-labelledby={`settings-${current.id}`}>
              <h2 id={`settings-${current.id}`}>{current.title ?? current.label}</h2>
              {current.render()}
            </section>
          )}
        </main>
      </div>
    </div>
  );
}

/** Consecutive sections with the same group, in order. */
function groups(sections: SettingsSection[]): { id: string; label?: string; sections: SettingsSection[] }[] {
  const out: { id: string; label?: string; sections: SettingsSection[] }[] = [];
  let key: string | undefined;
  for (const s of sections) {
    const last = out[out.length - 1];
    if (last && key === (s.group ?? "")) last.sections.push(s);
    else out.push({ id: `${out.length}:${s.group ?? ""}`, label: s.groupLabel, sections: [s] });
    key = s.group ?? "";
  }
  return out;
}

/** One labelled setting: a title and help text on the left, the control on the right (stacked on phones). */
export function SettingRow({ label, help, wide, children }: { label: string; help?: React.ReactNode; wide?: boolean; children: React.ReactNode }) {
  return (
    <div className={`setting-row${wide ? " wide" : ""}`}>
      <div className="setting-text">
        <div className="setting-label">{label}</div>
        {help && <div className="setting-help">{help}</div>}
      </div>
      <div className="setting-control">{children}</div>
    </div>
  );
}
